/**
 * Universal Groups — frontend types + the groupable-table map + typed fetchers.
 *
 * A group is a named collection; the objects (parcels, properties, clients, …) ARE
 * what is in it (a JOIN, not a follow). This module mirrors the backend contract:
 *   - `groups` CRUD                  → /api/groups[/:id]
 *   - the object↔group JOIN          → /api/group-items/*
 *   - a group's own custom fields    → /api/custom-fields/groups/:id (entityType 'groups')
 *
 * GROUPABLE_TYPE_BY_TABLE is the reverse of the backend GROUPABLE_TYPE_TABLE
 * (services/groups.js): plural detail-page TABLE → the SINGULAR entity_type the
 * join API expects. A detail page uses `groupableTypeForTable(table)` to decide
 * whether to show a "Groups" tab and what type to pass. Portal-only tables are
 * deliberately absent (Groups is internal only — spec FR-7).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useEntityPermission } from './usePermissions';

export interface Group {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  /** True for an admin-defined DEFAULT group (managed in Org Settings): protected
   *  from rename/delete here; those are done via Org Settings → Default groups. */
  is_system?: boolean;
  created_at: string;
  updated_at: string;
}

/** One object in a group (the join row). entity_type is the SINGULAR canonical form. */
export interface GroupItem {
  id: string;
  group_id: string;
  entity_type: string;
  entity_id: string;
  created_by: string | null;
  created_at: string;
  /** Human display label resolved server-side (the object's name/number); null if unavailable. */
  label?: string | null;
}

/** Plural detail-page table → the singular entity_type the /api/group-items join uses. */
export const GROUPABLE_TYPE_BY_TABLE: Record<string, string> = {
  parcels: 'parcel',
  properties: 'property',
  clients: 'client',
  appeals: 'appeal',
  tax_bills: 'tax_bill',
  tax_records: 'tax_record',
  invoices: 'invoice',
  invoice_items: 'invoice_item',
  assessors: 'assessor',
  collectors: 'collector',
  jurisdictions: 'jurisdiction',
  contacts: 'contact',
  documents: 'document',
  filed_returns: 'filed_return',
  information_requests: 'information_request',
  users: 'user',
};

/** The singular groupable entity_type for a detail-page table, or undefined if it can't be grouped. */
export function groupableTypeForTable(table: string): string | undefined {
  return GROUPABLE_TYPE_BY_TABLE[table];
}

/** Human labels for the singular entity_type (a group's items are grouped by these). */
export const GROUPABLE_TYPE_LABEL: Record<string, string> = {
  parcel: 'Parcels', property: 'Properties', client: 'Clients', appeal: 'Appeals',
  tax_bill: 'Tax bills', tax_record: 'Tax records', invoice: 'Invoices',
  invoice_item: 'Invoice items', assessor: 'Assessors', collector: 'Collectors',
  jurisdiction: 'Jurisdictions', contact: 'Contacts', document: 'Documents',
  filed_return: 'Filed returns', information_request: 'Information requests', user: 'Users',
};

/** The detail-page route base for a singular entity_type (so a group item links to its record). */
export const GROUPABLE_TYPE_ROUTE: Record<string, string> = {
  parcel: '/parcels', property: '/properties', client: '/clients', appeal: '/appeals',
  tax_bill: '/tax-bills', tax_record: '/tax-records', invoice: '/invoices',
  invoice_item: '/invoice-items', assessor: '/assessors', collector: '/collectors',
  jurisdiction: '/jurisdictions', contact: '/contacts', document: '/documents',
  filed_return: '/filed-returns', information_request: '/information-requests', user: '/users',
};

export const GROUPS_QK = {
  all: ['groups'] as const,
  one: (id: string) => ['groups', id] as const,
  items: (id: string) => ['group-items', 'group', id] as const,
  forEntity: (t: string, id: string) => ['group-items', 'entity', t, id] as const,
};

// ── Fetchers (thin, typed) ──────────────────────────────────────────────────
export const groupsApi = {
  list: (signal?: AbortSignal) => api.get<Group[] | { data?: Group[]; rows?: Group[] }>('/api/groups', signal),
  create: (body: { name: string; description?: string }) => api.post<Group>('/api/groups', body),
  rename: (id: string, body: { name?: string; description?: string }) => api.put<Group>(`/api/groups/${id}`, body),
  remove: (id: string) => api.delete(`/api/groups/${id}`),

  /** The groups a given object is in (feeds its Groups tab). */
  forEntity: (entityType: string, entityId: string, signal?: AbortSignal) =>
    api.get<{ entity_type: string; entity_id: string; groups: Group[] }>(`/api/group-items/entity/${entityType}/${entityId}`, signal),
  /** The objects in a given group (book-filtered by the server). */
  items: (groupId: string, signal?: AbortSignal) =>
    api.get<{ group_id: string; count: number; items: GroupItem[] }>(`/api/group-items/group/${groupId}`, signal),
  add: (groupId: string, entityType: string, entityId: string) =>
    api.post(`/api/group-items/${groupId}/${entityType}/${entityId}`),
  removeItem: (groupId: string, entityType: string, entityId: string) =>
    api.delete(`/api/group-items/${groupId}/${entityType}/${entityId}`),
};

/** Normalize the /api/groups list envelope (array | {data} | {rows}) to a plain array. */
export function asGroupArray(body: Group[] | { data?: Group[]; rows?: Group[] } | undefined): Group[] {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  return body.data || body.rows || [];
}

/**
 * The number of groups a record belongs to — for the Groups tab label, so it reads
 * "Groups (N)" like the other tabbed sections. Shares the SAME query key as
 * EntityGroupsPanel's own read (GROUPS_QK.forEntity), so there is no extra network
 * round-trip. Returns undefined while loading, when there is no id, or when the
 * caller cannot view groups (no count is shown in those cases).
 */
export function useGroupCount(entityType: string, entityId: string | undefined): number | undefined {
  const canView = useEntityPermission('group', 'can_view');
  const q = useQuery({
    queryKey: entityId ? GROUPS_QK.forEntity(entityType, entityId) : ['group-items', 'entity', 'none'],
    queryFn: ({ signal }) => groupsApi.forEntity(entityType, entityId as string, signal),
    enabled: !!entityId && canView,
    staleTime: 30_000,
  });
  return q.data?.groups?.length;
}

/** Tab label for a Groups tab: "Groups (N)" when N > 0, otherwise "Groups". */
export function groupTabLabel(count: number | undefined): string {
  return count && count > 0 ? `Groups (${count})` : 'Groups';
}
